import { useState, useEffect, MutableRefObject } from 'react';
import { BehaviorSubject } from 'rxjs';
import { addError } from './Error';
import { GridApiPro } from '@mui/x-data-grid-pro';

export interface TeamMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  imgUrl: string;
  phone: string;
  title: string;
}

export interface MyTeam {
  owner: TeamMember;
  success_manager: TeamMember;
}

export interface AdminState {
  selectedTab: number;
}

export interface GeneralState {
  myTeam: MyTeam;
  admin: AdminState;
  gridRefs: { [page: string]: MutableRefObject<GridApiPro> };
}

export interface UseGeneralStoreReturn {
  updateState: (data: Partial<GeneralState>) => void;
  clear: () => void;
  value: GeneralState;
}

export interface ApiAdvancedFilter {
  page?: number;
  pageSize?: number;
  sort?: string;
  direction?: string;
  logicalOperator?: string;
  filters?: FilterableField[];
}

export interface FilterableField {
  field: string;
  operator: string;
  value: string | number | string[] | number[];
}

export interface PaginatedResponse<T> {
  data: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export const defaultState: GeneralState = {
  myTeam: <MyTeam>{
    owner: {},
    success_manager: {}
  },
  admin: {
    selectedTab: Number(localStorage.getItem('adminTab')) || 0
  },
  gridRefs: {}
};

export enum InventoryPermission {
  ADD_ATTACHMENTS_INVENTORY = 'Add attachments to an inventory asset',
  ADD_INVENTORY_FLOW = 'Add inventory flow',
  CREATE_OUTSIDE_INVENTORY = 'Add outside inventory',
  EDIT_OUTSIDE_INVENTORY = 'Edit outside inventory',
  DELETE_OUTSIDE_INVENTORY = 'Delete outside inventory',
  ADD_CASES_ATTACHMENTS = 'Add attachments to an existing case',
  VIEW_PROJECTS = 'View Projects',
  VIEW_PROJECT_ATTACHMENTS = 'View Project Attachments',
  VIEW_PROJECT_CONTACTS = 'View Project Contacts',
  VIEW_PROJECT_COMMENTS = 'View Project Comments',
  CREATE_PROJECT_ATTACHMENTS = 'Create Project Attachments',
  CREATE_PROJECT_CONTACTS = 'Create Project Contacts',
  CREATE_PROJECT_COMMENTS = 'Create Project Comments',
  PARSE_INVOICE = 'Parse invoice'
}

export enum SupportCasePermission {
  VIEW_CASES = 'View support case page',
  VIEW_CASE_DETAILS = 'View support case details',
  CREATE_CASES_FROM_CASES = 'Create support cases from support cases page',
  CREATE_CASES_FROM_INVENTORY = 'Create support cases from the inventory page',
  VIEW_CASES_ON_INVENTORY = 'View support cases on inventory page',
  ADD_CASES_ATTACHMENTS = 'Add attachments to an existing case',
  ADD_ATTACHMENTS_INVENTORY = 'Add attachments to an inventory asset',
  EDIT_CASES_FROM_CASES = 'Edit support cases from the support cases page'
}

export enum AdminPermission {
  VIEW_ADMIN = 'View/access admin icon/areas',
  CREATE_USERS = 'Create Users',
  EDIT_USERS = 'Edit Users',
  VIEW_USERS = 'View Users',
  VIEW_USERS_ACCESS_LOG = 'View user access log',
  SOFT_DELETE_USERS = 'Soft Delete Users',
  IMPERSONATION = 'Impersonation',
  ASSIGN_TEAM = 'Assign Team',
  CREATE_ACCOUNT_CUSTOMIZATION = 'Create Account Customization',
  VIEW_ACCOUNT_CUSTOMIZATION = 'View Account Customization',
  BULK_OUTSIDE_INVENTORY = 'Validate and upload bulk outside inventory',
  VIEW_ADMIN_PAGE = 'View Admin Page',
  ADMIN_USERS_TAB = 'View Admin Users Tab',
  ADMIN_ACCOUNTS_TAB = 'View Admin Accounts Tab',
  ADMIN_TEAMS_TAB = 'View Admin Teams Tab',
  ADMIN_SUPPORT_TAB = 'View Admin Support Tab',
  EDIT_HIDDEN_INVENTORY = 'Edit Hidden Inventory'
}

export enum AdvisorPermission {
  VIEW_ADVISOR = 'View and interact with change client functionality',
  BULK_OUTSIDE_INVENTORY = 'Validate and upload bulk outside inventory'
}

export enum SdmPermission {
  VIEW_ADVISOR = 'View and interact with change client functionality',
  BULK_OUTSIDE_INVENTORY = 'Validate and upload bulk outside inventory',
  VIEW_ADMIN_PAGE = 'View Admin Page',
  ADMIN_ACCOUNTS_TAB = 'View Admin Accounts Tab',
  EDIT_HIDDEN_INVENTORY = 'Edit Hidden Inventory'
}

export enum CsmPermission {
  VIEW_ADVISOR = 'View and interact with change client functionality',
  BULK_OUTSIDE_INVENTORY = 'Validate and upload bulk outside inventory',
  VIEW_ADMIN_PAGE = 'View Admin Page',
  ADMIN_ACCOUNTS_TAB = 'View Admin Accounts Tab',
  EDIT_HIDDEN_INVENTORY = 'Edit Hidden Inventory'
}

export enum SupportPermission {
  VIEW_SUPPORT = 'View/access support icon/areas',
  CREATE_USERS = 'Create Users',
  EDIT_USERS = 'Edit Users',
  VIEW_USERS = 'View Users',
  VIEW_USERS_ACCESS_LOG = 'View user access log',
  SOFT_DELETE_USERS = 'Soft Delete Users',
  IMPERSONATION = 'Impersonation',
  ASSIGN_TEAM = 'Assign Team',
  CREATE_ACCOUNT_CUSTOMIZATION = 'Create Account Customization',
  BULK_OUTSIDE_INVENTORY = 'Validate and upload bulk outside inventory',
  UPDATE_SHARED_ACCOUNT_CUSTOMIZATION = 'Update Shared Account Customization',
  VIEW_ADMIN_PAGE = 'View Admin Page',
  ADMIN_USERS_TAB = 'View Admin Users Tab',
  ADMIN_ACCOUNTS_TAB = 'View Admin Accounts Tab',
  ADMIN_TEAMS_TAB = 'View Admin Teams Tab',
  ADMIN_SUPPORT_TAB = 'View Admin Support Tab',
  EDIT_HIDDEN_INVENTORY = 'Edit Hidden Inventory'
}

export enum ExecutivePermission {
  VIEW_EXECUTIVE = 'View/access executive icon/areas',
  BULK_OUTSIDE_INVENTORY = 'Validate and upload bulk outside inventory',
  VIEW_ADMIN_PAGE = 'View Admin Page',
  ADMIN_ACCOUNTS_TAB = 'View Admin Accounts Tab',
  EDIT_HIDDEN_INVENTORY = 'Edit Hidden Inventory'
}

export enum TeamManagerPermission {
  VIEW_TEAM = 'View/access team icon/areas',
  CREATE_TEAM_USERS = 'Create Team Users',
  EDIT_TEAM_USERS = 'Edit Team Users',
  VIEW_TEAM_USERS = 'View Team Users',
  SOFT_DELETE_TEAM_USERS = 'Soft Delete Team Users',
  TEAM_IMPERSONATION = 'Team Impersonation'
}

export enum AdvisePermission {
  ADVISE_VIEW = 'View Advise features',
  ADVISE_FULL = 'Full access to Advise features'
}

export enum CostIntelligencePermission {
  VIEW_PRICE_BENCHMARK = 'View Price Benchmark'
}

export enum CostAnalyticsPermission {
  VIEW_THOUGHTSPOT = 'ThoughtSpot'
}

export const Permission = {
  ...InventoryPermission,
  ...SupportCasePermission,
  ...AdminPermission,
  ...AdvisorPermission,
  ...TeamManagerPermission,
  ...SupportPermission,
  ...ExecutivePermission,
  ...AdvisePermission,
  ...CostIntelligencePermission,
  ...CostAnalyticsPermission
};

// The pair the hidden-assets endpoint checks before it accepts a hide.
export const HIDE_RECORD_PERMISSIONS = [Permission.ADMIN_ACCOUNTS_TAB, Permission.EDIT_HIDDEN_INVENTORY];

const subject = new BehaviorSubject<GeneralState>(defaultState);

export const updateState = (update: Partial<GeneralState>) => {
  const currentState = { ...subject.getValue() };
  subject.next({ ...currentState, ...update });

  if (update?.admin && 'selectedTab' in update.admin) {
    localStorage.setItem('adminTab', String(update?.admin?.selectedTab));
  }
};

export const updateGridRef = (page: string, ref: MutableRefObject<GridApiPro>) => {
  const currentStateUpdate = { ...subject.getValue() };

  currentStateUpdate.gridRefs = {
    ...currentStateUpdate.gridRefs,
    [page]: ref
  };

  subject.next(currentStateUpdate);
};

export const getState = () => subject.getValue();

export const getGeneralStoreSubject = (): BehaviorSubject<GeneralState> => subject;

const emitErrorState = (message?: string): void => addError('general', message);

export const useGeneralStore = () => {
  const [currentState, setCurrentState] = useState(defaultState);

  useEffect(() => {
    const subscription = subject.subscribe((message: GeneralState) => {
      setCurrentState(message);
    });

    return () => {
      if (subscription) subscription.unsubscribe();
    };
  }, [subject]);

  const updateState = (val: Partial<GeneralState>) => {
    const update = { ...currentState, ...val };
    subject.next(update);
  };
  const setErrorMessageData = (message: string | undefined) => emitErrorState(message);

  const clear = () => subject.next(defaultState);

  return { currentState, updateState, clear, setErrorMessageData };
};
